import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SentryService } from '@services/sentry.service';
import { deepClone } from '../utils/deep-clone.utils';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(private sentryService: SentryService) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Extract request context
        const context = {
          url: request.url,
          method: request.method,
          headers: this.sanitizeHeaders(
            request.headers.keys().reduce((acc, key) => {
              // Don't include authorization headers in Sentry
              if (key.toLowerCase() !== 'authorization') {
                acc[key] = request.headers.get(key);
              }
              return acc;
            }, {} as Record<string, string | null>),
          ),
          body: this.sanitizeRequestBody(request.body),
          status: error.status,
          statusText: error.statusText,
        };

        // Determine error severity based on status code
        let severity = 'error';
        if (error.status >= 500) {
          severity = 'fatal';
        } else if (error.status === 404) {
          severity = 'warning';
        } else if (error.status === 401 || error.status === 403) {
          severity = 'error';
        } else if (error.status >= 400) {
          severity = 'warning';
        }

        // Capture the error with context
        this.sentryService.captureException(error, context);

        // Return the error for further handling
        return throwError(() => error);
      }),
    );
  }

  /**
   * Sanitize request body to remove sensitive information
   */
  private sanitizeRequestBody(body: any): any {
    if (!body) return null;

    try {
      // Create a deep copy to avoid modifying the original
      const sanitized = deepClone(body);

      // List of sensitive fields to redact
      const sensitiveFields = [
        'password',
        'token',
        'secret',
        'creditCard',
        'cardNumber',
        'cvv',
      ];

      // Recursively sanitize the object
      this.redactSensitiveData(sanitized, sensitiveFields);

      return sanitized;
    } catch (e) {
      // If we can't stringify/parse (e.g., for FormData), return a simple object
      return { type: typeof body, sanitized: true };
    }
  }

  /**
   * Recursively redact sensitive data in an object
   */
  private redactSensitiveData(obj: any, sensitiveFields: string[]): void {
    if (!obj || typeof obj !== 'object') return;

    Object.keys(obj).forEach((key) => {
      if (
        sensitiveFields.some((field) =>
          key.toLowerCase().includes(field.toLowerCase()),
        )
      ) {
        obj[key] = '[REDACTED]';
      } else if (typeof obj[key] === 'object') {
        this.redactSensitiveData(obj[key], sensitiveFields);
      }
    });
  }

  /**
   * Sanitize headers to remove sensitive information
   */
  private sanitizeHeaders(
    headers: Record<string, string | null>,
  ): Record<string, string | null> {
    const sensitiveHeaders = ['authorization', 'cookie', 'x-auth-token'];

    Object.keys(headers).forEach((key) => {
      if (sensitiveHeaders.some((h) => key.toLowerCase().includes(h))) {
        headers[key] = '[REDACTED]';
      }
    });

    return headers;
  }
}
